import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Encouragement } from 'src/app/data/patient/domain/encouragement/encouragement';
import { EncouragementCommands } from 'src/app/data/patient/domain/encouragement/encouragement_commands';
import { Diary, DiaryProps } from 'src/app/data/tracker/domain/diary/diary';
import { DiaryCommands } from 'src/app/data/tracker/domain/diary/diary_commands';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { DietitianStateProvider } from '../../../../data/dietitian/domain/dietitian_state_provider';
import { Patient } from '../../../../data/patient/domain/patient';
import { PatientStateProvider } from '../../../../data/patient/domain/patient_state_provider';
import { DiaryTypeCommands } from '../../../../data/tracker/domain/diary_type/diary_type_commands';
import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';

export interface PatientInfoViewModel {
  isLoading: boolean;
  error: string;
  patient: Patient;
}

export interface AccountViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

@Injectable()
export class DiaryFacade {
  constructor(
    private diaryCommands: DiaryCommands,
    private diaryTypeCommands: DiaryTypeCommands,
    private encouragementCommands: EncouragementCommands,
    private dietitianStateProvider: DietitianStateProvider,
    private patientStateProvider: PatientStateProvider,
  ) {
    // do nothing
  }

  get accountStatus$(): Observable<AccountViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <AccountViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  get patientStatus$(): Observable<PatientInfoViewModel> {
    return this.patientStateProvider.state$.pipe(
      map((e) => {
        return <PatientInfoViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          patient: e.entity,
        };
      }),
    );
  }

  getPatientDiaries(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Diary[]> {
    return this.diaryCommands.getPatientDiaries(patientId, start, end);
  }

  getPatientEncouragements(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Encouragement[]> {
    return this.encouragementCommands.getPatientEncouragements(
      patientId,
      start,
      end,
    );
  }

  updateDiaryComment(diaryId: string, props: DiaryProps, comment?: string) {
    return this.diaryCommands.saveDiary(
      {
        ...props,
        comment,
      },
      diaryId,
    );
  }

  async findAllDiaryType(
    dietitianId: string | undefined,
    patientId: string | undefined,
  ): Promise<DiaryType[]> {
    return this.diaryTypeCommands.findAllDiaryTypes(
      dietitianId,
      undefined,
      patientId,
    );
  }
}
